import React, { useCallback, useEffect, useState, useRef, Component } from 'react';
import { CheckCircle2 } from 'lucide-react';
// --- Data ---
const PRODUCTS = [{
  id: 'guide',
  title: 'Guía Maestra Mil Hojas Pro',
  editionLabel: 'PDF Digital',
  priceText: '$9.00',
  thumbnailUrl: null,
  emoji: '🍰',
  weight: 0.6
}, {
  id: 'fillings',
  title: 'Pack Rellenos 5 Minutos',
  editionLabel: 'Recetas Extra',
  priceText: '$7.00',
  thumbnailUrl: 'https://cdn.magicpatterns.com/uploads/ooQiVK2GkF8tQH3CF1Puer/order_bump_1.png',
  weight: 0.25
}, {
  id: 'planner',
  title: 'Planner de Porciones',
  editionLabel: 'Medidas Exactas',
  priceText: '$7.00',
  thumbnailUrl: 'https://cdn.magicpatterns.com/uploads/m6XCZXf7mSyFNXtbgpKzK4/order_bump_2.png',
  weight: 0.15
}];
const NAMES = ['María G.', 'Carlos R.', 'Ana P.', 'Luis M.', 'Sofía T.', 'Diego H.', 'Valentina L.', 'Andrés C.', 'Camila S.', 'Fernando B.', 'Isabella R.', 'Javier N.', 'Lucía F.', 'Miguel A.', 'Paula V.', 'Roberto D.', 'Daniela M.', 'Alejandro K.', 'Gabriela O.', 'Santiago E.', 'Mariana J.', 'Eduardo W.', 'Natalia Q.', 'Sebastián I.', 'Carolina Z.', 'Ricardo U.', 'Elena P.', 'Tomás G.', 'Adriana L.', 'Nicolás R.', 'Juana B.', 'Mateo S.', 'Valeria D.', 'Jorge L.', 'Clara M.'];
const CITIES = ['Miami', 'Houston', 'Los Angeles', 'New York', 'Chicago', 'Dallas', 'San Antonio', 'Phoenix', 'San Diego', 'Orlando', 'Atlanta', 'Denver', 'Las Vegas', 'San Jose', 'Austin', 'Tampa', 'Charlotte', 'Nashville', 'Portland', 'Seattle', 'San Francisco', 'Boston', 'Philadelphia', 'Fort Worth'];
// --- Helper Functions ---
const getRandomProduct = () => {
  const r = Math.random();
  if (r < 0.6) return PRODUCTS[0];
  if (r < 0.85) return PRODUCTS[1];
  return PRODUCTS[2];
};
const getRandomItem = <T,>(arr: T[]): T => arr[Math.floor(Math.random() * arr.length)];
// --- Component ---
export function RecentPurchaseToast() {
  const [isVisible, setIsVisible] = useState(false);
  const [toastData, setToastData] = useState<{
    name: string;
    city: string;
    product: (typeof PRODUCTS)[0];
  } | null>(null);
  // Keep track of recent names to avoid repeats
  const nameHistoryRef = useRef<string[]>([]);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const showNextToast = useCallback(() => {
    // 1. Select Product
    const product = getRandomProduct();
    // 2. Select Name (avoiding last 10)
    const availableNames = NAMES.filter(n => !nameHistoryRef.current.includes(n));
    // Fallback to full list if we ran out (unlikely with 30 names and history of 10)
    const namePool = availableNames.length > 0 ? availableNames : NAMES;
    const name = getRandomItem(namePool);
    // Update history
    nameHistoryRef.current.push(name);
    if (nameHistoryRef.current.length > 10) {
      nameHistoryRef.current.shift();
    }
    // 3. Select City
    const city = getRandomItem(CITIES);
    // 4. Set Data & Show
    setToastData({
      name,
      city,
      product
    });
    setIsVisible(true);
    // 5. Schedule Hide (after 4s visible)
    timeoutRef.current = setTimeout(() => {
      setIsVisible(false);
      // 6. Schedule Next Show (after hide animation + random delay 8-15s)
      // Hide animation takes ~300ms, let's add that to the delay
      const randomDelay = Math.floor(Math.random() * (15000 - 8000 + 1)) + 8000;
      timeoutRef.current = setTimeout(showNextToast, 300 + randomDelay);
    }, 4000);
  }, []);
  useEffect(() => {
    // Initial start after 3 seconds
    timeoutRef.current = setTimeout(showNextToast, 3000);
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [showNextToast]);
  if (!toastData) return null;
  return <div className={`fixed bottom-4 left-1/2 z-40 w-[90%] max-w-[360px] -translate-x-1/2 transform transition-all duration-500 ease-in-out ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0 pointer-events-none'}`} role="status" aria-live="polite" data-id="element-369">
      <div className="flex items-center gap-3 rounded-xl border border-stone-100 bg-white p-3 shadow-lg shadow-stone-200/50" data-id="element-370">
        {/* Image / Icon */}
        <div className="relative flex h-10 w-10 flex-shrink-0 items-center justify-center overflow-hidden rounded-lg bg-stone-50 border border-stone-100" data-id="element-371">
          {toastData.product.thumbnailUrl ? <img src={toastData.product.thumbnailUrl} alt="" className="h-full w-full object-cover" data-id="element-372" /> : <span className="text-xl" role="img" aria-label="cake" data-id="element-373">
              {toastData.product.emoji}
            </span>}
          {/* Green check badge */}
          <div className="absolute -bottom-1 -right-1 rounded-full bg-white" data-id="element-374">
            <CheckCircle2 className="h-4 w-4 fill-green-500 text-white" data-id="element-375" />
          </div>
        </div>

        {/* Text Content */}
        <div className="flex-1 min-w-0" data-id="element-376">
          <p className="text-sm font-medium text-stone-900 leading-tight truncate" data-id="element-377">
            <span className="font-bold" data-id="element-378">{toastData.name}</span> en{' '}
            {toastData.city}
          </p>
          <p className="text-xs text-stone-500 mt-0.5 truncate" data-id="element-379">
            Compró {toastData.product.title}
          </p>
          <p className="text-[10px] font-semibold text-green-600 mt-0.5" data-id="element-380">
            {toastData.product.editionLabel} — {toastData.product.priceText}
          </p>
        </div>
      </div>
    </div>;
}